import React, { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CBadge,
  CButton,
  CCallout,
  CFormLabel,
  CFormSelect,
  CSmartTable,
} from "@coreui/react-pro";
import EmployerPageFrame from "../../components/employer/EmployerPageFrame";
import useEmployerOsWorkspace from "../../hooks/useEmployerOsWorkspace";
import { buildCandidateCaseRows, buildComplianceRows } from "../../services/employer/employerCaseOsDomain";
import { formatEmployerAdFocusLabel } from "../../utils/employerAdLabels";
import {
  buildEmployerTrustBlockerRows,
  buildEmployerTrustIncidentRows,
  buildEmployerTrustOpsSummary,
  buildEmployerTrustSummary,
  buildEmployerTrustTaskRows,
  getEmployerTrustStatusColor,
  getTrustIncidentStatusColor,
  getTrustTaskStatusColor,
} from "../../services/employer/employerTrustDomain";

export default function EmployerCompliancePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspace = useEmployerOsWorkspace({ preloadDetails: true, preloadFiling: true });
  const requestedAdId = searchParams.get("adId") || "";

  useEffect(() => {
    if (requestedAdId) workspace.setSelectedAdId(requestedAdId);
  }, [requestedAdId, workspace.setSelectedAdId]);

  const caseRows = useMemo(
    () => buildCandidateCaseRows({
      ads: workspace.ads,
      applications: workspace.applications,
      detailsById: workspace.detailsById,
      filingById: workspace.filingById,
    }),
    [workspace.ads, workspace.applications, workspace.detailsById, workspace.filingById]
  );
  const complianceRows = useMemo(() => buildComplianceRows({ caseRows }), [caseRows]);
  const trustSummary = useMemo(() => buildEmployerTrustSummary(workspace.profile || {}), [workspace.profile]);
  const trustBlockerRows = useMemo(() => buildEmployerTrustBlockerRows(workspace.profile || {}), [workspace.profile]);
  const trustOpsSummary = useMemo(() => buildEmployerTrustOpsSummary(workspace.profile || {}), [workspace.profile]);
  const trustTaskRows = useMemo(() => buildEmployerTrustTaskRows(workspace.profile || {}), [workspace.profile]);
  const trustIncidentRows = useMemo(() => buildEmployerTrustIncidentRows(workspace.profile || {}), [workspace.profile]);

  return (
    <EmployerPageFrame
      title="Filing & Compliance"
      subtitle="Pre-Hire Eligibility, Anerkennungsfahrplan, Dokumentenluecken und Arbeitgeberpflichten laufen jetzt als eigene operative Surface mit klaren Gates."
      actions={(
        <>
          <CButton color="secondary" variant="outline" onClick={() => workspace.refreshWorkspace()}>
            Aktualisieren
          </CButton>
          <CButton
            color="primary"
            variant="outline"
            onClick={() => navigate(`/dashboard/employer/documents${workspace.selectedAdId ? `?adId=${workspace.selectedAdId}${workspace.isTraining ? "&training=1" : ""}` : ""}`)}
          >
            Dokumente
          </CButton>
        </>
      )}
      contentClassName="space-y-4"
      shellProps={{
        shellLabel: "Employer Compliance OS",
        shellDescription: "Eligibility, Filing-Readiness und regulatorische Pflichten werden als echte Betriebslogik gefuehrt.",
        topContext: "Filing & Compliance",
      }}
    >
      {workspace.error ? (
        <CCallout color="danger">
          <strong>Compliance-Fläche konnte nicht vollstaendig geladen werden.</strong>
          <div>{workspace.error}</div>
        </CCallout>
      ) : null}

      <section className="dashboard-card-shell space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="dashboard-kicker">Filing Readiness</span>
            <h2 className="dashboard-card-title">Case-Gates statt versteckter Filing-Technik</h2>
            <p className="dashboard-section-copy">Diese Surface fuehrt sichtbar ueber harte Blocker, regulatorische Risiken, Dokumentenluecken und den naechsten zulaessigen Schritt je Case.</p>
          </div>
          <div className="min-w-[260px]">
            <CFormLabel>Anzeige</CFormLabel>
            <CFormSelect
              value={workspace.selectedAdId}
              onChange={(event) => workspace.setSelectedAdId(event.target.value)}
              options={(workspace.ads || []).map((entry) => ({
                label: formatEmployerAdFocusLabel(entry),
                value: String(entry.id),
              }))}
            />
          </div>
        </div>

        {complianceRows.some((entry) => entry.blockerCount > 0) || trustBlockerRows.length ? (
          <CCallout color="warning">
            <strong>Blockerfuehrung ist aktiv.</strong>
            <div>
              {complianceRows.filter((entry) => entry.blockerCount > 0).length} Cases haben im aktuellen Fokus mindestens einen harten Filing-Blocker aus Pflichtunterlagen, Consent oder unklarer Pruefung.
              {trustBlockerRows.length ? ` Zusaetzlich blockieren ${trustBlockerRows.length} Employer-Trust-Blocker die Live-Faehigkeit.` : ""}
            </div>
          </CCallout>
        ) : (
          <CCallout color="success">
            <strong>Keine harten Filing-Blocker im aktuellen Fokus.</strong>
            <div>Warnungen und Folgeaktionen bleiben trotzdem sichtbar, damit Readiness nicht dekorativ wird.</div>
          </CCallout>
        )}

        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="mb-3 d-flex justify-content-between gap-3">
            <div>
              <span className="dashboard-kicker">Employer Trust</span>
              <h3 className="dashboard-card-title mb-0">Trust-Blocker laufen in dieselbe Compliance-Fuehrung</h3>
            </div>
            <CBadge color={getEmployerTrustStatusColor(trustSummary.trustStatus)}>{trustSummary.trustStatusLabel}</CBadge>
          </div>
          {trustBlockerRows.length ? (
            <CSmartTable
              columns={[
                { key: "blocker", label: "Trust-Blocker" },
                { key: "priority", label: "Prioritaet" },
                { key: "nextAction", label: "Naechste Aktion" },
              ]}
              items={trustBlockerRows}
              pagination={false}
              sorter={false}
              tableProps={{ hover: true, responsive: true }}
            />
          ) : (
            <p className="mb-0 text-sm text-slate-600">Keine aktiven Employer-Trust-Blocker. Filing und Publishing lesen aktuell aus demselben verifizierten Profil.</p>
          )}
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="dashboard-kicker">Review Tasks</span>
            <h3 className="dashboard-card-title">Offene Trust-Tasks</h3>
            {trustTaskRows.length ? (
              <CSmartTable
                columns={[
                  { key: "title", label: "Task" },
                  { key: "statusLabel", label: "Status" },
                  { key: "blocking", label: "Blockiert" },
                  { key: "nextAction", label: "Naechste Aktion" },
                ]}
                items={trustTaskRows.filter((entry) => entry.status !== "completed")}
                pagination={false}
                sorter={false}
                tableProps={{ hover: true, responsive: true }}
                scopedColumns={{
                  statusLabel: (item) => (
                    <td>
                      <CBadge color={getTrustTaskStatusColor(item.status)}>{item.statusLabel}</CBadge>
                    </td>
                  ),
                }}
              />
            ) : (
              <p className="mb-0 text-sm text-slate-600">Keine offenen Trust-Tasks.</p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <span className="dashboard-kicker">Incidents</span>
            <h3 className="dashboard-card-title">Aktive Employer-Incidents</h3>
            {trustIncidentRows.length ? (
              <CSmartTable
                columns={[
                  { key: "typeLabel", label: "Incident" },
                  { key: "severity", label: "Severity" },
                  { key: "statusLabel", label: "Status" },
                  { key: "summary", label: "Summary" },
                ]}
                items={trustIncidentRows.filter((entry) => entry.status !== "resolved" && entry.status !== "rejected")}
                pagination={false}
                sorter={false}
                tableProps={{ hover: true, responsive: true }}
                scopedColumns={{
                  statusLabel: (item) => (
                    <td>
                      <CBadge color={getTrustIncidentStatusColor(item.status)}>{item.statusLabel}</CBadge>
                    </td>
                  ),
                }}
              />
            ) : (
              <p className="mb-0 text-sm text-slate-600">Keine aktiven Incidents.</p>
            )}
            <p className="mb-0 mt-3 text-sm text-slate-600">
              Workflow: {trustOpsSummary.reviewWorkflowLabel} · Investigation: {trustOpsSummary.underInvestigation ? "ja" : "nein"} · Risiko: {trustOpsSummary.riskLevel}
            </p>
          </div>
        </div>

        {complianceRows.length ? (
          <CSmartTable
            columns={[
              { key: "candidateRef", label: "Candidate" },
              { key: "adTitle", label: "Job" },
              { key: "readinessLabel", label: "Case-Status" },
              { key: "filingStage", label: "Filing" },
              { key: "blockerCount", label: "Blocker" },
              { key: "missingDocumentCount", label: "Pflichtunterlagen offen" },
              { key: "consentGapCount", label: "Consent-Luecken" },
              { key: "warningCount", label: "Pruefung / Warnungen" },
              { key: "recognitionStatus", label: "Anerkennung" },
              { key: "languageGap", label: "Sprache" },
              { key: "nextAction", label: "Naechste Aktion" },
            ]}
            items={complianceRows}
            pagination={false}
            sorter={false}
            tableProps={{ hover: true, responsive: true }}
            scopedColumns={{
              readinessLabel: (item) => (
                <td>
                  <CBadge color={item.readinessColor}>{item.readinessLabel}</CBadge>
                </td>
              ),
            }}
          />
        ) : (
          <CCallout color="info">
            <strong>Keine Compliance-Cases im aktuellen Fokus.</strong>
            <div>Beginne im Bereich Anzeigen oder Kandidaten, damit Filing & Compliance reale Case-Daten lesen kann.</div>
          </CCallout>
        )}
      </section>
    </EmployerPageFrame>
  );
}
